import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useQueue } from '../hooks/useQueue';
import { useSettings } from '../contexts/SettingsContext';
import { RoleShell } from '../components/RoleShell';
import { CopperButton } from '../components/ui/copper-button';
import { StatusPill } from '../components/ui/status-pill';
import { toast } from 'sonner';
import { Receipt, IndianRupee, Wallet, CreditCard, Banknote, CheckCircle2, Sparkles, Tag } from 'lucide-react';

const PAYMENT_METHODS = [
  { code: 'cash', label: 'Cash',     icon: Banknote },
  { code: 'upi',  label: 'UPI',      icon: Sparkles },
  { code: 'card', label: 'Card',     icon: CreditCard },
];

const Particle = ({ delay, angle, distance }) => (
  <span
    style={{
      position: 'absolute',
      left: '50%',
      top: '50%',
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #D4784A, #B5612E)',
      transform: `translate(-50%, -50%) rotate(${angle}deg) translateX(${distance}px)`,
      opacity: 0,
      animation: `particleBurst 900ms ease-out ${delay}ms forwards`,
      pointerEvents: 'none',
    }}
  />
);

export const BillingView = () => {
  const { token, user } = useAuth();
  const { settings } = useSettings();
  const { patients, loading, updatePatient } = useQueue({ token });
  const [selectedId, setSelectedId] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [confirming, setConfirming] = useState(false);
  const [burst, setBurst] = useState(false);
  const [discount, setDiscount] = useState(0);

  const pending = patients.filter(p => p.status === 'payment-pending');
  const recentPaid = patients.filter(p => p.status === 'completed' && p.paid).slice(-5).reverse();
  const current = patients.find(p => p.id === selectedId) || pending[0];
  const dueAfterDiscount = Math.max(0, (current?.amount_due || 0) - discount);

  const totalPending = pending.reduce((sum, p) => sum + (p.amount_due || 0), 0);
  const totalCollectedToday = patients
    .filter(p => p.paid && p.amount_due)
    .reduce((sum, p) => sum + p.amount_due, 0);

  // Reset discount when switching patients
  React.useEffect(() => { setDiscount(0); }, [current?.id]);

  const handleConfirm = async () => {
    if (!current) return;
    setConfirming(true);
    setBurst(true);
    try {
      await updatePatient(current.id, {
        status: 'completed',
        paid: true,
        payment_method: paymentMethod,
        amount_due: dueAfterDiscount,
        discount_amount: discount,
      });
      toast.success(`₹${dueAfterDiscount} collected from ${current.patient_name} via ${paymentMethod.toUpperCase()}${discount > 0 ? ` · ₹${discount} discount applied` : ''}`);
      setSelectedId(null);
      setDiscount(0);
      setTimeout(() => setBurst(false), 1200);
    } catch (e) {
      toast.error('Payment failed');
    } finally {
      setConfirming(false);
    }
  };

  return (
    <RoleShell titlePrefix="Billing" userName={user?.name || 'Anil Kumar'}>
      <style>{`
        @keyframes particleBurst {
          0%   { opacity: 1; transform: translate(-50%, -50%) rotate(var(--a, 0deg)) translateX(0); }
          100% { opacity: 0; transform: translate(-50%, -50%) rotate(var(--a, 0deg)) translateX(140px); }
        }
      `}</style>

      <div className="flex" style={{ height: 'calc(100vh - 64px)' }} data-testid="billing-view">
        {/* Pending list */}
        <div className="glass-primary overflow-y-auto" style={{ width: '380px', padding: '20px' }}>
          <div className="grid grid-cols-2 gap-2 mb-5">
            <div className="glass-primary rounded-[var(--radius-md)] p-3.5">
              <div className="font-['Inter'] text-xs" style={{ color: '#78716C' }}>Pending</div>
              <div className="font-['DM_Mono'] text-[26px] font-medium" style={{ color: '#F59E0B' }} data-testid="billing-pending-total">
                ₹{totalPending}
              </div>
            </div>
            <div className="glass-primary rounded-[var(--radius-md)] p-3.5">
              <div className="font-['Inter'] text-xs" style={{ color: '#78716C' }}>Collected today</div>
              <div className="font-['DM_Mono'] text-[26px] font-medium" style={{ color: '#16A34A' }} data-testid="billing-collected-total">
                ₹{totalCollectedToday}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-3">
            <Receipt size={16} strokeWidth={1.75} style={{ color: 'var(--color-slate)' }} />
            <h2 className="font-['Sora'] text-sm font-semibold" style={{ color: '#1C1917' }}>Awaiting Payment</h2>
            <span className="ml-auto px-2 py-0.5 rounded-full font-['DM_Mono'] text-[11px] font-semibold" style={{ background: 'rgba(245,158,11,0.10)', color: '#F59E0B' }}>
              {pending.length}
            </span>
          </div>

          {loading && pending.length === 0 && (
            <div className="font-['Inter'] text-sm" style={{ color: '#78716C' }}>Loading…</div>
          )}
          {!loading && pending.length === 0 && (
            <div className="font-['Inter'] text-sm" style={{ color: '#78716C' }}>No pending payments right now.</div>
          )}

          <div className="flex flex-col gap-2 mb-6">
            {pending.map(p => {
              const isSelected = current?.id === p.id;
              return (
                <button
                  key={p.id}
                  onClick={() => setSelectedId(p.id)}
                  className="text-left p-3 rounded-[var(--radius-md)] transition-all duration-180"
                  style={{
                    background: isSelected ? 'rgba(245, 158, 11, 0.08)' : 'rgba(255,255,255,0.5)',
                    border: isSelected ? '1px solid rgba(245, 158, 11, 0.45)' : '1px solid rgba(28,25,23,0.06)'
                  }}
                  data-testid={`billing-item-${p.token_number}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-['DM_Mono'] text-sm" style={{ color: '#F59E0B' }}>{p.token_number}</span>
                    <span className="font-['DM_Mono'] text-base font-semibold" style={{ color: '#1C1917' }}>₹{p.amount_due || 0}</span>
                  </div>
                  <div className="font-['Sora'] text-sm font-semibold" style={{ color: '#1C1917' }}>{p.patient_name}</div>
                </button>
              );
            })}
          </div>

          <div className="text-overline mb-2">RECENT PAYMENTS</div>
          <div className="flex flex-col gap-1.5">
            {recentPaid.map(p => (
              <div key={p.id} className="flex items-center justify-between p-2 rounded-[var(--radius-sm)]" style={{ background: 'rgba(22,163,74,0.04)' }}>
                <span className="font-['DM_Mono'] text-xs" style={{ color: '#16A34A' }}>{p.token_number}</span>
                <span className="font-['Inter'] text-xs" style={{ color: '#44403C' }}>{p.patient_name}</span>
                <span className="font-['DM_Mono'] text-xs font-semibold" style={{ color: '#16A34A' }}>₹{p.amount_due}</span>
              </div>
            ))}
            {recentPaid.length === 0 && <div className="font-['Inter'] text-xs" style={{ color: '#A8A29E' }}>No payments yet</div>}
          </div>
        </div>

        {/* Right pane */}
        <div className="flex-1 overflow-y-auto" style={{ background: 'var(--color-cream)', padding: '28px 32px' }}>
          {!current && (
            <div className="glass-primary rounded-[var(--radius-2xl)] p-16 text-center">
              <Wallet size={42} strokeWidth={1.5} style={{ color: 'var(--color-slate)', opacity: 0.4, margin: '0 auto 16px' }} />
              <h2 className="font-['Sora'] text-xl font-bold mb-2" style={{ color: '#1C1917' }}>All clear!</h2>
              <p className="font-['Inter'] text-sm" style={{ color: '#78716C' }}>No payments waiting right now.</p>
            </div>
          )}

          {current && (
            <div className="max-w-2xl">
              <div className="glass-primary rounded-[var(--radius-2xl)] p-8 mb-5">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-['DM_Mono'] text-sm font-semibold" style={{ color: '#F59E0B' }}>{current.token_number}</span>
                      <StatusPill status={current.status} />
                    </div>
                    <h1 className="font-['Sora'] text-3xl font-bold" style={{ color: '#1C1917' }}>{current.patient_name}</h1>
                    <p className="font-['Inter'] text-sm mt-1" style={{ color: '#78716C' }}>{current.patient_phone}</p>
                  </div>
                  <div className="text-right">
                    <div className="font-['Inter'] text-xs" style={{ color: '#78716C' }}>AMOUNT DUE</div>
                    <div className="font-['DM_Mono'] text-5xl font-semibold flex items-center gap-1 mt-1" style={{ color: '#B5612E' }}>
                      <IndianRupee size={36} strokeWidth={2.5} />
                      <span data-testid="amount-due">{current.amount_due || 0}</span>
                    </div>
                  </div>
                </div>

                {current.doctor_notes && (
                  <div className="rounded-[10px] p-3 mb-4" style={{ background: 'rgba(61,90,128,0.04)' }}>
                    <div className="text-overline mb-1" style={{ color: '#78716C' }}>DR. NOTES</div>
                    <div className="font-['Inter'] text-sm" style={{ color: '#44403C' }}>{current.doctor_notes}</div>
                  </div>
                )}

                <div className="text-overline mb-3">PAYMENT METHOD</div>
                <div className="grid grid-cols-3 gap-3 mb-5">
                  {PAYMENT_METHODS.map(m => {
                    const Icon = m.icon;
                    const active = paymentMethod === m.code;
                    return (
                      <button
                        key={m.code}
                        onClick={() => setPaymentMethod(m.code)}
                        className="p-4 rounded-[var(--radius-lg)] flex flex-col items-center gap-2 transition-all duration-180"
                        style={{
                          background: active ? 'rgba(61, 90, 128, 0.08)' : 'rgba(255,255,255,0.5)',
                          border: active ? '2px solid var(--color-slate)' : '2px solid rgba(28,25,23,0.06)'
                        }}
                        data-testid={`payment-${m.code}`}
                      >
                        <Icon size={22} strokeWidth={1.75} style={{ color: active ? 'var(--color-slate)' : '#78716C' }} />
                        <span className="font-['Sora'] text-sm font-semibold" style={{ color: active ? 'var(--color-slate)' : '#44403C' }}>
                          {m.label}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Discount + final due summary */}
                <div className="rounded-[var(--radius-lg)] p-4 mb-5" style={{ background: 'rgba(124, 58, 237, 0.05)', border: '1px solid rgba(124, 58, 237, 0.15)' }}>
                  <div className="flex items-center justify-between mb-3">
                    <label className="font-['Sora'] text-[14px] font-semibold flex items-center gap-2" style={{ color: '#7C3AED' }}>
                      <Tag size={14} strokeWidth={1.75} />
                      Apply discount
                    </label>
                    <div className="flex items-center gap-2">
                      <span className="font-['DM_Mono'] text-sm" style={{ color: '#78716C' }}>₹</span>
                      <input
                        type="number"
                        min="0"
                        max={current.amount_due || 0}
                        value={discount || ''}
                        onChange={(e) => setDiscount(Math.min(Math.max(0, parseFloat(e.target.value) || 0), current.amount_due || 0))}
                        placeholder="0"
                        className="w-24 h-9 px-3 rounded-[10px] bg-white font-['DM_Mono'] text-sm font-semibold outline-none text-right"
                        style={{ color: '#1C1917', border: '1px solid rgba(124, 58, 237, 0.25)' }}
                        data-testid="billing-discount-input"
                      />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {[10, 20, 50].map(pct => {
                      const v = Math.round(((current.amount_due || 0) * pct) / 100);
                      return (
                        <button
                          key={pct}
                          type="button"
                          onClick={() => setDiscount(v)}
                          className="px-2.5 py-1 rounded-full font-['Sora'] text-[11px] font-semibold transition-all duration-180 hover:scale-[1.05]"
                          style={{ background: 'rgba(124, 58, 237, 0.10)', color: '#7C3AED' }}
                        >
                          {pct}% · ₹{v}
                        </button>
                      );
                    })}
                    {discount > 0 && (
                      <button
                        type="button"
                        onClick={() => setDiscount(0)}
                        className="px-2.5 py-1 rounded-full font-['Sora'] text-[11px] font-semibold ml-auto"
                        style={{ background: 'rgba(120, 113, 108, 0.10)', color: '#78716C' }}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  {discount > 0 && (
                    <div className="mt-3 pt-3 border-t flex items-center justify-between" style={{ borderColor: 'rgba(124, 58, 237, 0.18)' }}>
                      <span className="font-['Inter'] text-[13px]" style={{ color: '#7C3AED' }}>Patient pays</span>
                      <span className="font-['DM_Mono'] text-xl font-semibold" style={{ color: '#7C3AED' }} data-testid="billing-due-after-discount">
                        ₹{dueAfterDiscount}
                      </span>
                    </div>
                  )}
                </div>

                <div className="relative">
                  {burst && (
                    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                      {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((a, i) => (
                        <Particle key={a} angle={a} distance={140} delay={i * 30} />
                      ))}
                    </div>
                  )}
                  <button
                    onClick={handleConfirm}
                    disabled={confirming}
                    className="btn-success-3d relative w-full h-16 rounded-[var(--radius-xl)] font-['Sora'] text-base font-bold flex items-center justify-center gap-2"
                    data-testid="confirm-payment-btn"
                  >
                    <CheckCircle2 size={20} strokeWidth={2} />
                    {confirming ? 'Processing…' : `Confirm payment · ₹${dueAfterDiscount}`}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </RoleShell>
  );
};

export default BillingView;
